import { useCallback, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'
import { api, ApiError } from '../lib/api'
import { recordServerTime } from '../lib/clock'
import type { DraftResponse, DraftState } from '../types/protocol'
import SettingsForm from '../components/SettingsForm'
import RosterConfigEditor from '../components/RosterConfigEditor'
import TeamListEditor from '../components/TeamListEditor'
import StartPanel from '../components/StartPanel'
import RankingsUpload from '../components/RankingsUpload'
import LotteryPanel from '../components/LotteryPanel'

// The commissioner's setup console: everything that has to be settled before
// the first pick. The URL's token is the credential — it is sent as a header
// on every request and never stored anywhere else.
//
// Every form here is guarded to `setup` by DraftEngine, so once the draft is
// running this page has nothing left to offer and hands off to /control. That
// keeps the recovery link the admin dashboard hands out (`/setup#<token>`)
// working whatever the draft's status is.
export default function Setup() {
  // The token rides in the fragment, which browsers never put in the request
  // line — so no log at any layer (Rails, Thruster, kamal-proxy, Cloudflare)
  // can hold it. A Rails-side filter would only have cleaned one of the four.
  const commissionerToken = decodeURIComponent(useLocation().hash.slice(1))
  const [draft, setDraft] = useState<DraftState | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    try {
      const response = await api<DraftResponse>('/drafts/resolve', { commissionerToken })
      // The only server-time sample this page will ever get. recordServerTime is
      // fed from cable events everywhere else, and Setup has no cable — without
      // this the countdown and every MIN_LEAD comparison run at offset 0 for the
      // page's whole life, so a laptop with a wrong clock schedules a lottery at
      // the wrong instant and shows a wrong T-minus with total confidence.
      recordServerTime(response.server_now_ms)
      setDraft(response.draft)
      setError(null)
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 401
          ? 'This setup link is not valid. Double-check the URL you were given.'
          : 'Could not load the draft. Is the server up?',
      )
    }
  }, [commissionerToken])

  // No realtime until Phase 6 — refresh when the tab regains focus so
  // claimed badges catch up.
  useEffect(() => {
    void refetch()
    const onFocus = () => void refetch()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refetch])

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 text-center">
        <p className="max-w-sm text-ink-mid">{error}</p>
      </main>
    )
  }

  if (!draft) {
    return (
      <main className="flex min-h-screen items-center justify-center text-sm text-ink-lo">
        Loading…
      </main>
    )
  }

  if (draft.status !== 'setup') {
    return <Navigate to={`/control#${commissionerToken}`} replace />
  }

  return (
    <main className="min-h-screen">
      <div className="mx-auto max-w-3xl space-y-5 px-4 py-10">
        {/* The console's own header. It stays put while the sections below
            scroll, so the code you are reading out loud is always on screen. */}
        <header className="sticky top-0 z-20 -mx-4 flex flex-wrap items-end justify-between gap-3 border-b border-line bg-surface-2/95 px-4 py-4 shadow-raised backdrop-blur-sm">
          <div className="min-w-0">
            <p className="eyebrow">Commissioner setup</p>
            <h1 className="mt-1 truncate font-display text-3xl leading-none font-bold text-ink-hi">
              {draft.name}
            </h1>
          </div>
          <p className="shrink-0 rounded-full border border-signal/30 bg-signal/10 px-4 py-1.5 font-mono text-lg font-semibold tracking-[0.2em] text-signal">
            {draft.join_code}
          </p>
        </header>

        <SettingsForm draft={draft} commissionerToken={commissionerToken} onDraft={setDraft} />
        <RosterConfigEditor draft={draft} commissionerToken={commissionerToken} onDraft={setDraft} />
        <TeamListEditor draft={draft} commissionerToken={commissionerToken} onDraft={setDraft} />
        <LotteryPanel draft={draft} commissionerToken={commissionerToken} onDraft={setDraft} />
        <RankingsUpload
          draft={draft}
          commissionerToken={commissionerToken}
          onCountChange={(count) =>
            setDraft((prev) => (prev ? { ...prev, rankings_count: count } : prev))
          }
        />
        <StartPanel draft={draft} commissionerToken={commissionerToken} />
      </div>
    </main>
  )
}
